import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiContext } from "../../../context/MyContext";
import { get as commsGet, post as commsPost } from "../../../service/comms/commsApi";
import {
  createTicket,
  getSupportMetrics,
  getViewCounts,
} from "../../../service/comms/supportService";
import { Toaster } from "../../../common/Toaster/Toaster";
import { Drawer } from "../../../common/comms/Drawer";
import { useCompanyId } from "../../../hooks/comms/useCompanyId";
import {
  markTicketSeen,
  requestSupportResync,
} from "../../../hooks/comms/RoomsUnreadProvider";
import { useOversightAccess } from "../../../hooks/comms/OversightAccessProvider";
import { SupportOpsBar } from "./SupportOpsBar";
import { SupportViewsRail } from "./SupportViewsRail";
import { TicketList } from "./TicketList";
import { TicketPane } from "./TicketPane";
import { RequesterRail } from "./RequesterRail";
import CreateTicketModal from "./CreateTicketModal";
import { useCachedList } from "../../../hooks/useCachedList";
import { supportCountsCache } from "../../../utils/listCache/caches";
import { useSupportLayout } from "./useSupportLayout";
import { useSupportQueue } from "./useSupportQueue";
import { useTicketWorkspace } from "./useTicketWorkspace";
import { extractViewCounts } from "./supportUtils";
import { getRoomId } from "./ticketUtils";

/**
 * The unified support workspace — saved views, ticket list, thread and
 * requester context on one screen.
 *
 * One screen rather than a queue page plus a separate ticket page: opening a
 * ticket used to navigate *away* from the queue, so an agent lost their place on every
 * triage step, and leads had a separate dashboard that answered different
 * questions from the same data. One screen serves both — the caller's role
 * decides which controls are live, and the backend scopes what they can see.
 *
 * Routes: `/support`, `/support/console`, `/support/tickets/:id` — the id only
 * selects a row, so the queue never unmounts.
 */
export default function SupportWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { empId } = useContext(ApiContext);
  const { companyId } = useCompanyId();
  const { isStaff } = useOversightAccess();
  const layout = useSupportLayout();

  const [selectedRoomId, setSelectedRoomId] = useState(id || null);
  const [search, setSearch] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  // Page-level feedback (ticket created, routing rule saved). Ticket actions
  // have their own toast inside useTicketWorkspace.
  const [pageToast, setPageToast] = useState(null);

  const notify = useCallback(
    (message, type = "success") => setPageToast({ message, type, id: Date.now() }),
    []
  );

  // Staff triage the whole desk; everyone else starts on their own tickets.
  const queue = useSupportQueue({ initialView: isStaff ? "all_open" : "mine", search });

  // Follow the URL on direct nav / back-forward.
  useEffect(() => {
    setSelectedRoomId(id || null);
  }, [id]);

  // The chip counts. Cached so the ops bar and the rail paint immediately on
  // re-entry, then revalidated whenever the queue changes underneath them —
  // these numbers are the authority the row-level merge defers to, so they
  // are always re-read from the server rather than derived locally.
  const countsFetcher = useCallback(
    () => getViewCounts(commsGet).then(extractViewCounts),
    []
  );
  const { data: countsData, refresh: refreshCounts } = useCachedList({
    cache: supportCountsCache,
    key: "counts",
    fetcher: countsFetcher,
  });
  const counts = countsData || { views: {}, departments: {} };

  const countsVersionRef = useRef(queue.version);
  useEffect(() => {
    if (queue.version === countsVersionRef.current) return;
    countsVersionRef.current = queue.version;
    refreshCounts();
  }, [queue.version, refreshCounts]);

  useEffect(() => {
    let active = true;
    getSupportMetrics(commsGet)
      // Staff-only — a non-staff caller 403s and simply gets dashes.
      .then((data) => active && setMetrics(data))
      .catch((err) => console.error("Failed to load support metrics:", err));
    return () => {
      active = false;
    };
  }, []);

  // Any successful ticket action re-reads the queue and the counts behind it.
  // Depends on `queue.refresh` rather than `queue` so it doesn't churn on
  // every render of the list.
  const { refresh: refreshQueue } = queue;
  const handleTicketChanged = useCallback(() => {
    refreshQueue();
    refreshCounts();
  }, [refreshQueue, refreshCounts]);

  const ticket = useTicketWorkspace(selectedRoomId, empId, handleTicketChanged);

  const handleSelect = useCallback(
    (row) => {
      const roomId = getRoomId(row) || row?.roomId;
      if (!roomId) return;
      setSelectedRoomId(roomId);
      // `replace` so triaging a queue doesn't bury the back button under every
      // ticket the agent glanced at.
      navigate(`/support/tickets/${roomId}`, { replace: true });
    },
    [navigate]
  );

  const handleBackToList = useCallback(() => {
    setSelectedRoomId(null);
    navigate("/support/console", { replace: true });
  }, [navigate]);

  const handleCreate = useCallback(
    async (payload) => {
      const created = await createTicket(commsPost, { company_id: companyId, ...payload });
      // Don't alert the creator about their own ticket.
      markTicketSeen(getRoomId(created?.data?.ticket || created?.data || created));
      requestSupportResync();
      notify("Ticket created.");
      handleTicketChanged();
    },
    [companyId, handleTicketChanged, notify]
  );

  const clientId =
    ticket.detail?.client_id ||
    ticket.detail?.ticket?.client_id ||
    ticket.detail?.client?.id ||
    null;

  const requesterRail = (
    <RequesterRail
      clientId={clientId}
      roomId={selectedRoomId}
      detail={ticket.detail}
      loading={ticket.loading}
      error={ticket.error}
    />
  );

  const ticketPane = (
    <TicketPane
      {...ticket.centerProps}
      viewerUserId={empId}
      headerTicket={queue.selected(selectedRoomId)}
      onBack={layout.stacked ? handleBackToList : undefined}
      onOpenRail={layout.showContextRail ? undefined : () => setRailOpen(true)}
    />
  );

  const listPane = (
    <TicketList
      queue={queue}
      counts={counts}
      selectedRoomId={selectedRoomId}
      onSelect={handleSelect}
      agents={ticket.agents}
      onChanged={handleTicketChanged}
    />
  );

  // Below 1024 only one pane is on screen at a time — the ticket replaces the
  // list rather than squeezing beside it.
  const showTicketOnly = layout.stacked && selectedRoomId;

  return (
    <div className="flex h-screen min-h-0 w-full flex-col overflow-hidden bg-white">
      <SupportOpsBar
        counts={counts}
        metrics={metrics}
        search={search}
        onSearchChange={setSearch}
        onNewTicket={() => setCreateOpen(true)}
      />

      <div className="flex min-h-0 flex-1">
        {layout.showViewsRail && (
          <SupportViewsRail
            view={queue.view}
            onSelectView={queue.setView}
            department={queue.filters.department}
            onSelectDepartment={(d) => queue.setFilter("department", d)}
            counts={counts}
            companyId={companyId}
            onFeedback={notify}
          />
        )}

        {!showTicketOnly && (
          <div
            className={`flex min-w-0 flex-col border-r border-gray-200 ${
              layout.stacked
                ? "flex-1"
                : selectedRoomId
                ? "w-[46%] max-w-[720px] flex-shrink-0"
                : "flex-1"
            }`}
          >
            {!layout.showViewsRail && (
              <SupportViewsRail
                variant="compact"
                view={queue.view}
                onSelectView={queue.setView}
                department={queue.filters.department}
                onSelectDepartment={(d) => queue.setFilter("department", d)}
                counts={counts}
                companyId={companyId}
              />
            )}
            {listPane}
          </div>
        )}

        {selectedRoomId && (
          <div className="flex min-w-0 flex-1 flex-col">{ticketPane}</div>
        )}

        {layout.showContextRail && selectedRoomId && (
          <div className="w-[272px] flex-shrink-0 overflow-hidden border-l border-gray-200">
            {requesterRail}
          </div>
        )}
      </div>

      {!layout.showContextRail && (
        <Drawer
          isOpen={railOpen}
          onClose={() => setRailOpen(false)}
          title="Requester context"
        >
          <RequesterRail
            clientId={clientId}
            roomId={selectedRoomId}
            detail={ticket.detail}
            loading={ticket.loading}
            error={ticket.error}
            layout="flow"
          />
        </Drawer>
      )}

      <CreateTicketModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      {ticket.actionModal}
      {ticket.toast && (
        <Toaster
          key={ticket.toast.id}
          message={ticket.toast.message}
          type={ticket.toast.type}
        />
      )}
      {pageToast && (
        <Toaster key={pageToast.id} message={pageToast.message} type={pageToast.type} />
      )}
    </div>
  );
}
